import {
  configureStore,
  createListenerMiddleware,
  TypedStartListening,
} from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

import {
  activeCasesApi,
  aircraftStatusesApi,
  hubsResourceReportApi,
  newCasesApi,
  simpleStatisticsApi,
  surgeonsApi,
} from '../api';
import { toolbarSlice } from '../slices';
import { widgetManagerSlice } from '../widgetManager';

import { buildDefaultTemplates } from './defaultTemplates';
import { registerMigrationListeners } from './migrationListeners';
import { persistConfig } from './persistConfig';
import { rootReducer, type RootState } from './rootReducer';
import { registerSchemaFailureListeners } from './schemaFailureListeners';

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const listenerMiddleware = createListenerMiddleware();

export type AppStartListening = TypedStartListening<
  RootState,
  ReturnType<typeof useDispatch>
>;
export const startAppListening =
  listenerMiddleware.startListening as AppStartListening;

/**
 * Build the initial preloaded state with CC Alpha template as default
 * @returns Complete preloaded state for Redux store
 */
const buildPreloadedState = () => {
  const { templates, defaultLayout, defaultTemplateMetadata } =
    buildDefaultTemplates();

  return {
    ...defaultLayout,
    toolbar: toolbarSlice.getInitialState(),
    widgetManager: widgetManagerSlice.getInitialState(),
    template: {
      templates: {
        ids: templates.map(t => t.id),
        entities: Object.fromEntries(templates.map(t => [t.id, t])),
      },
      activeTemplateId: defaultTemplateMetadata.id,
      activeTemplateName: defaultTemplateMetadata.name,
      activeTemplateIsCustom: defaultTemplateMetadata.isCustom,
      previewTemplateId: null,
      isApplying: false,
    },
  };
};

export const initStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    preloadedState: buildPreloadedState(),
    middleware: getDefault =>
      getDefault({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
        immutableCheck: {
          warnAfter: 128,
        },
      })
        .prepend(listenerMiddleware.middleware)
        .concat(
          newCasesApi.middleware,
          hubsResourceReportApi.middleware,
          activeCasesApi.middleware,
          aircraftStatusesApi.middleware,
          surgeonsApi.middleware,
          simpleStatisticsApi.middleware,
        ),
    devTools:
      import.meta.env.REACT_APP_ENV === 'local' ||
      import.meta.env.REACT_APP_ENV === 'development',
  });

  const persistor = persistStore(store);

  // Register listeners after store is initialized
  registerMigrationListeners(startAppListening);
  registerSchemaFailureListeners(startAppListening);

  return { store, persistor };
};

export type AppStore = ReturnType<typeof initStore>['store'];
export type AppDispatch = AppStore['dispatch'];
